import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { axios } from "@/client/libs/axios";

import { fetchPushConfig } from "./api";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  WEB PUSH (CLIENT) — abonnement / désabonnement VAPID
 * ─────────────────────────────────────────────────────────────────────────────
 *  Flux :
 *   1. GET /api/notifications/push/config → clé publique VAPID + support
 *   2. navigator.serviceWorker.register('/sw.js') (vite-plugin-pwa autoUpdate)
 *   3. Notification.requestPermission() (UX progressive côté UI)
 *   4. pushManager.subscribe({ userVisibleOnly, applicationServerKey })
 *   5. PATCH /api/notifications/push/subscribe avec la subscription JSON
 *
 *  La désinscription supprime l'endpoint côté serveur puis unsubscribes.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Décode une clé VAPID base64url (applicationServerKey attend un Uint8Array). */
export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export type PushState = {
  /** Navigateur ET serveur prêts pour le push. */
  supported: boolean;
  /** Service Worker enregistré et actif. */
  installed: boolean;
  permission: NotificationPermission | "unsupported";
  subscribed: boolean;
  busy: boolean;
};

export const usePushNotifications = () => {
  const { data: config, isFetched: configFetched } = useQuery({
    queryKey: ["push-config"],
    queryFn: fetchPushConfig,
    staleTime: Infinity,
  });

  const browserSupported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  /** Le serveur expose des clés VAPID valides. */
  const serverEnabled = Boolean(config?.supported);

  const [state, setState] = useState<PushState>({
    supported: false,
    installed: false,
    permission: "unsupported",
    subscribed: false,
    busy: false,
  });

  /**
   * État navigateur résolu (première synchronisation terminée). Avec la
   * résolution de la config serveur, forme `ready` : les consommateurs qui
   * programment un timer (push-nudge, #116) doivent attendre ce signal pour
   * ne pas armer sur un état provisoire puis voir leurs dépendances « churner ».
   */
  const [synced, setSynced] = useState(false);

  /**
   * Synchronise l'état depuis le navigateur. Utilise `getRegistration` (résout
   * immédiatement, `undefined` si aucun SW) et JAMAIS `serviceWorker.ready`
   * qui reste en attente indéfiniment quand le SW n'est pas encore enregistré.
   */
  const syncFromBrowser = useCallback(async () => {
    if (!browserSupported) {
      setState({
        supported: false,
        installed: false,
        permission: "unsupported",
        subscribed: false,
        busy: false,
      });
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration("/");
    const permission = Notification.permission;
    let subscribed = false;

    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      subscribed = Boolean(subscription);
    }

    setState({
      supported: true,
      installed: Boolean(registration),
      permission,
      subscribed,
      busy: false,
    });
  }, [browserSupported]);

  useEffect(() => {
    // Dès que la config serveur est connue (VAPID) — le SW est enregistré au
    // démarrage de l'app (registerServiceWorker), la sync est donc fiable.
    if (!serverEnabled) return;
    void syncFromBrowser()
      .catch(() => null)
      .finally(() => setSynced(true));
  }, [serverEnabled, syncFromBrowser]);

  /** Attend l'activation effective du Service Worker avant tout pushManager.* */
  const ensureActivation = async (
    registration: ServiceWorkerRegistration,
  ): Promise<ServiceWorkerRegistration> => {
    if (registration.active) return registration;

    const worker = registration.installing ?? registration.waiting;
    if (!worker) {
      // Fallback : `ready` est désormais sûr — un register vient d'avoir lieu
      return navigator.serviceWorker.ready;
    }

    await new Promise<void>((resolve) => {
      worker.addEventListener("statechange", () => {
        if (worker.state === "activated") resolve();
      });
    });
    return registration;
  };

  /** Demande la permission puis abonne et enregistre côté serveur. */
  const subscribe = useCallback(async () => {
    if (!browserSupported) return { ok: false as const, reason: "unsupported" };
    if (!serverEnabled || !config?.publicKey)
      return { ok: false as const, reason: "server_disabled" };

    setState((s) => ({ ...s, busy: true }));
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState((s) => ({ ...s, permission, busy: false, subscribed: false }));
        return { ok: false as const, reason: permission };
      }

      // Enregistrement idempotent + activation effective du SW
      const registered = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      const registration = await ensureActivation(registered);

      // Ancien abonnement (autre clé VAPID ?) → nettoyage avant re-souscription
      const existing = await registration.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();

      const applicationServerKey: BufferSource = urlBase64ToUint8Array(
        config.publicKey,
      ) as unknown as BufferSource;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Envoi du JSON au serveur (endpoint + keys)
      await axios.patch("/notifications/push/subscribe", JSON.parse(JSON.stringify(subscription)));

      setState((s) => ({ ...s, permission, subscribed: true, installed: true, busy: false }));
      return { ok: true as const };
    } catch (error) {
      setState((s) => ({ ...s, busy: false, subscribed: false }));
      return { ok: false as const, reason: String(error) };
    }
  }, [browserSupported, serverEnabled, config?.publicKey]);

  /** Désabonne l'appareil côté navigateur + serveur. */
  const unsubscribe = useCallback(async () => {
    if (!browserSupported) {
      return { ok: false as const, reason: "unsupported" };
    }

    setState((s) => ({ ...s, busy: true }));
    try {
      const registration = await navigator.serviceWorker.getRegistration("/");
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await axios.delete("/notifications/push/unsubscribe", {
          data: { endpoint: subscription.endpoint },
        });
        await subscription.unsubscribe();
      }
      setState((s) => ({ ...s, subscribed: false, busy: false }));
      return { ok: true as const };
    } catch (error) {
      setState((s) => ({ ...s, busy: false }));
      return { ok: false as const, reason: String(error) };
    }
  }, [browserSupported]);

  /** Re-synchronise l'état depuis le navigateur (après un changement des réglages). */
  const retry = useCallback(async () => {
    await syncFromBrowser();
  }, [syncFromBrowser]);

  /** L'état complet est résolu : config serveur fetchée + sync navigateur faite. */
  const ready = configFetched && (synced || !serverEnabled);

  return { ...state, browserSupported, serverEnabled, ready, subscribe, unsubscribe, retry };
};
